import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { Label, UncontrolledTooltip } from 'reactstrap';
import { useIntl } from 'react-intl';

import UpgradePlanButton from '../UpgradePlanButton';

import { isMini, isMiniAdobeIntegration } from '../../helpers/App';
import { userHasFeature } from '../../helpers/Features';
import { getSetting } from '../../helpers/Settings';
import { sendAdobePostMessage } from '../../helpers/Adobe';

const FileStatusSelect = ({
  profile,
  share,
  label,
  fileStatuses,
  defaultValue,
  handleChange,
  selectedFile,
  selectedFiles,
  forceRequiredError,
  scope,
}) => {
  const intl = useIntl();

  const [selectedStatus, setSelectedStatus] = useState({
    value: defaultValue ? defaultValue.id : '',
    label: defaultValue ? defaultValue.name : intl.formatMessage({ defaultMessage: '-- no status --' }),
    color: defaultValue ? defaultValue.color : null,
  });

  useEffect(() => {
    if (scope === 'upload') return;

    handleChange({
      target: {
        name: 'fileStatus',
        value: selectedStatus.value,
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getOptions = () => {
    const options = [];

    options.push(
      {
        value: 'EMPTY_SELECTION',
        label: '',
        color: null,
        isHidden: true,
      },
    );

    if (!(userHasFeature('file-status') && getSetting('file_status_required'))) {
      if (defaultValue && defaultValue.id === -2) {
        options.push({
          value: '',
          label: '',
          color: null,
        });
      } else {
        options.push({
          value: '',
          label: intl.formatMessage({ defaultMessage: '-- no status --' }),
          color: null,
        });
      }
    }

    fileStatuses.forEach((status) => {
      options.push({
        value: status.id,
        label: status.name,
        color: status.color,
        action: status.action,
      });
    });

    return options;
  };

  const handleChangeValue = (value) => {
    setSelectedStatus(value);
    handleChange({
      target: {
        name: 'fileStatus',
        value: value.value,
      },
    });
  };

  const statusRequired = (selectedFile || selectedFiles) && (userHasFeature('file-status') && getSetting('file_status_required'));
  const statusValidationError = forceRequiredError || (selectedFile ?
    selectedFile.errors?.status :
    !!selectedFiles?.find(f => f.errors?.status));

  const getStatusDisplayValue = () => {
    if (selectedFile) {
      return selectedFile.status?.id || '';
    }

    if (selectedFiles && selectedFiles.length > 0) {
      const firstStatus = selectedFiles[0].status;
      if (firstStatus && selectedFiles.every(sf => sf.status?.id === firstStatus.id)) {
        return firstStatus.id;
      }
      if (!firstStatus && selectedFiles.every(sf => !sf.status)) {
        return '';
      }

      return 'MIXED_STATUS';
    }

    if (defaultValue) {
      return defaultValue.id;
    }

    return null;
  };

  const statusDisplayValue = getStatusDisplayValue();

  const displayValue = (statusDisplayValue !== null && statusDisplayValue !== 'MIXED_STATUS') ?
    getOptions().find(s => s.value === statusDisplayValue) : null;

  return (
    <>
      <Label for="selectFileStatus" className="with-upgrade-button">
        {label || intl.formatMessage({ defaultMessage: 'Status' })}
        {statusRequired && (
          <span
            className="required-asterisk"
            id="status-required-asterisk"
          >
            *
            <UncontrolledTooltip fade={false} placement="bottom" target="status-required-asterisk">
              {intl.formatMessage({ defaultMessage: 'Required' })}
            </UncontrolledTooltip>
          </span>
        )}
        <UpgradePlanButton
          feature="file-status"
          simple={true}
        />
      </Label>
      {statusValidationError && (
        <div className="invalid-feedback" style={{ display: 'block' }}>
          {intl.formatMessage({ defaultMessage: 'This field is required' })}
        </div>
      )}
      <div className="new-status-link">
        {isMini() ? (
          <a
            href={`${window.location.origin}/admin/settings/file-statuses`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              if (isMiniAdobeIntegration()) {
                sendAdobePostMessage('open-link', `https://${profile?.client?.domain}/admin/settings/file-statuses`);
              }
            }}
          >
            {intl.formatMessage({ defaultMessage: 'Create a Status' })}
          </a>
        )
          : (
            <Link to="/admin/settings/file-statuses" target="_blank" rel="noreferrer">
              {intl.formatMessage({ defaultMessage: 'Create a Status' })}
            </Link>
          )}
      </div>
      <Select
        id="ownerSelect"
        inputId="selectFileStatus"
        name="fileStatus"
        className="file-status-select"
        classNamePrefix="file-status-select"
        getOptionLabel={e => (
          <div className="file-status-select-option" style={{ display: 'flex', alignItems: 'center' }}>
            {e.color && (
              <i className="file-status" style={{ backgroundColor: e.color }} />
            )}
            <div
              className="file-status-select-option-name-wrapper"
            >
              <span className="file-status-name">{e.label}</span>
              {e.action && (
              <>
                {e.action === 'disable_download' && (
                <span className="file-status-action">
                  {intl.formatMessage({ defaultMessage: 'Disable downloading' })}
                </span>
                )}
                {e.action === 'watermark' && (
                <span className="file-status-action">
                  {intl.formatMessage({ defaultMessage: 'Add watermark' })}
                </span>
                )}
              </>
              )}
            </div>
          </div>
        )}
        options={getOptions().filter(option => !option.isHidden)}
        onChange={handleChangeValue}
        {...(scope !== 'upload' ? { defaultValue: selectedStatus } : {})}
        {...(scope === 'upload' ? { value: displayValue || { value: 'EMPTY_SELECTION', label: '', color: null } } : {})}
        isSearchable={false}
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
        isDisabled={!userHasFeature('file-status') && (!share || !share.status_change)}
      />
    </>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  fileStatuses: state.Files.statuses,
  share: state.Shares.share,
});

FileStatusSelect.propTypes = {
  profile: PropTypes.object,
  fileStatuses: PropTypes.array.isRequired,
  share: PropTypes.object,
  label: PropTypes.string,
  defaultValue: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  selectedFile: PropTypes.object,
  selectedFiles: PropTypes.array,
  forceRequiredError: PropTypes.bool,
  scope: PropTypes.string,
};

FileStatusSelect.defaultProps = {
  profile: null,
  share: null,
  label: '',
  defaultValue: null,
  selectedFile: null,
  selectedFiles: [],
  forceRequiredError: false,
  scope: 'default',
};

export default connect(mapStateToProps)(FileStatusSelect);
